import { View, ViewOrgVisibility } from "../models/View";
import { ViewList as ViewListModel } from "../models/ViewList";
import React, { useEffect, useRef, useState } from "react";
import Lang from "../lib/Language";
import Log from "../lib/Log";
import API from "../lib/TimeEditAPI";
import ConfirmButton from "./ConfirmButton";
import _ from "underscore";
import { useAppContext } from "../contexts/AppContext";

const ViewVisibility = {
    PRIVATE: 0,
    PUBLIC: 1,
    ORGANIZATIONS: 2,
};

type TViewListProps = {
    data: ViewListModel;
    view: View;
    onViewChange: (id: number) => void;
};

type TViewListState = {
    isDeletable: boolean;
    isModifiable: boolean;
    canMakePublic?: boolean;
    canMakePrivate?: boolean;
};

const getViewName = (callback, defaultName = "") => {
    // eslint-disable-next-line no-alert
    const name = window.prompt(Lang.get("cal_res_side_view_set_name"), defaultName);
    if (name) {
        callback(name);
    }
};

const ViewList = (props: TViewListProps) => {
    const appContext = useAppContext();
    const [state, setState] = useState<TViewListState>({
        isDeletable: false,
        isModifiable: false,
    });

    const isMounted = useRef(false);

    const updateViews = () => {
        props.data.updateViews((viewList: ViewListModel) =>
            appContext.update(props.data, viewList)
        );
    };

    const updateModifiable = (id) => {
        API.okToPublishView(id, (res) => {
            API.okToChangeViews(id, (res2) => {
                if (isMounted.current && res2.parameters[0].length > 0) {
                    setState({
                        canMakePublic: res.parameters[0],
                        canMakePrivate: res.parameters[1],
                        isDeletable: res2.parameters[0][0].delete,
                        isModifiable: res2.parameters[0][0].modify,
                    });
                }
            });
        });
    };

    useEffect(() => {
        isMounted.current = true;
        updateViews();
        updateModifiable(props.view ? props.view.id : null);
        return () => {
            isMounted.current = false;
        };
    }, []);

    // componentDidUpdate refreshed permissions only when the selected view
    // changed identity, so the previous id is tracked rather than using deps.
    const prevViewId = useRef<number | null | undefined>(undefined);
    useEffect(() => {
        const currentId = props.view ? props.view.id : null;
        if (prevViewId.current === undefined) {
            prevViewId.current = currentId;
            return;
        }
        if (prevViewId.current !== null && currentId !== null && prevViewId.current === currentId) {
            return;
        }
        prevViewId.current = currentId;
        updateModifiable(currentId);
    });

    const changeView = (id: number, event?: Event) => {
        if (event && _.isModKey(event)) {
            const view = _.find(props.data.views, (item) => item.id === id);
            // eslint-disable-next-line no-alert
            if (confirm(Lang.get("cal_res_side_view_do_you_want_to_delete_the_view", view.name))) {
                deleteView(id);
            }
            return;
        }
        props.onViewChange(id);
    };

    const saveView = (saveNew, makePublic: boolean | null | undefined = null) => {
        const callback = (view) => {
            const newView = props?.view?.section?.toJSON();
            let orgState = saveNew ? null : props.view.orgState;
            if (makePublic === true) {
                orgState = ViewOrgVisibility.PUBLIC;
            }
            if (makePublic === false) {
                orgState = ViewOrgVisibility.PRIVATE;
            }
            // Make new views private by not copying organizations.
            View.save(
                view.id,
                view.name,
                newView,
                saveNew ? [] : props.view.organizations,
                orgState,
                (result) => {
                    if (result[0].details) {
                        Log.warning(result[0].details);
                        return;
                    }
                    if (saveNew || (makePublic !== null && makePublic !== undefined)) {
                        updateViews();
                        changeView(result[0].reference);
                    }
                    Log.info(Lang.get("nc_cal_res_side_view_saved", view.name));
                }
            );
        };

        if (saveNew) {
            getViewName((name) => {
                callback({ id: 0, name });
            }, props.view.name);
            return;
        }

        const view = _.find(props.data.views, (item) => item.id === props.view.id);
        callback(view);
    };

    const renameView = () => {
        const view = _.find(props.data.views, (item) => item.id === props.view.id);
        getViewName((name) => {
            View.save(
                view.id,
                name,
                props.view.section?.toJSON(),
                props.view.organizations,
                props.view.orgState,
                (result) => {
                    if (result[0].details) {
                        Log.warning(result[0].details);
                        return;
                    }
                    updateViews();
                    Log.info(Lang.get("nc_cal_res_side_view_saved", name));
                }
            );
        }, view.name);
    };

    function deleteView(id = props.view.id) {
        const view = _.find(props.data.views, (item) => item.id === id);
        View.delete(view.id, () => {
            Log.info(Lang.get("nc_cal_res_side_view_deleted", view.name));
            updateViews();
            changeView(1);
        });
    }

    const isStandardViewSelected = () => props.view && props.view.id === 1;

    const views: any[] = [].concat(props.data.views);
    views.sort((a, b) => {
        // Standard view should be on top
        if (a.id === 1 || b.id === 1) {
            return a.id - b.id;
        }

        // Public views above private views
        if (a.orgState !== ViewVisibility.PRIVATE && b.orgState === ViewVisibility.PRIVATE) {
            return -1;
        } else if (a.orgState === ViewVisibility.PRIVATE && b.orgState !== ViewVisibility.PRIVATE) {
            return 1;
        }

        // Otherwise sort by name
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
        } else if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
        }
        return 0;
    });

    const makePublicButton =
        props.view && props.view.orgState === ViewVisibility.PRIVATE && state.canMakePublic ? (
            <button onClick={() => saveView(false, true)} className="deprecated save">
                {Lang.get("layer_make_public")}
            </button>
        ) : null;
    const makePrivateButton =
        props.view &&
        props.view.orgState === ViewVisibility.PUBLIC &&
        state.canMakePrivate &&
        !isStandardViewSelected() ? (
            <button onClick={() => saveView(false, false)} className="deprecated save">
                {Lang.get("layer_make_private")}
            </button>
        ) : null;

    const isUnsupported = props.view ? props.view.isUnsupported : false;
    return (
        <div className="viewPane">
            <h2>{Lang.get("permission_views")}</h2>
            <ul className="selectBox viewList">
                {views.map((el, index, array) => {
                    const classes = {
                        rowItem: true,
                        selected: el.id === (props.view ? props.view.id : null),
                    };

                    const li = (
                        <li key={el.id} className={_.classSet(classes)}>
                            <a onClick={() => changeView(el.id, undefined)}>{el.name}</a>
                        </li>
                    );

                    if (
                        el.orgState === ViewVisibility.PRIVATE &&
                        array[index - 1] &&
                        array[index - 1].orgState !== ViewVisibility.PRIVATE
                    ) {
                        return [<li key="sep" className="separator" />, li];
                    }

                    return li;
                })}
            </ul>

            <div className="btnGroup vertical" key={"this.props.data.selected"}>
                {makePublicButton}
                {makePrivateButton}
                <button
                    onClick={renameView}
                    disabled={!state.isModifiable || isUnsupported}
                    className="deprecated save"
                >
                    {Lang.get("cal_header_popup_period_change_name")}
                </button>
                <button
                    onClick={() => saveView(false, undefined)}
                    disabled={!state.isModifiable || isUnsupported}
                    className="deprecated save"
                >
                    {Lang.get("cal_res_side_view_update_title")}
                </button>
                <button onClick={() => saveView(true, undefined)} className="deprecated save">
                    {Lang.get("nc_cal_res_side_view_save_as_title")}
                </button>
            </div>
            <div className="btnGroup vertical">
                <ConfirmButton
                    key={props.view ? props.view.id : 0}
                    className="remove"
                    onClick={() => deleteView()}
                    disabled={!state.isDeletable}
                    confirmMessage={Lang.get(
                        "cal_res_side_view_do_you_want_to_delete_the_view",
                        props.view ? props.view.name : "No view"
                    )}
                >
                    {Lang.get("cal_header_popup_multi_object_remove")}
                </ConfirmButton>
            </div>
        </div>
    );
};

export default ViewList;
